import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreens } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { difference } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { LightRepeatableListItem } from 'screens/AssessmentScreen/Repeatable/LightRepeatableListItem';
import {
  doAseReorderChildGroups,
  doAseSetChildGroups,
  eventReorderScreenDone,
  NAVIGATE_MODAL,
} from 'store/actions';

import {
  selectAseTplFieldToGroup,
  selectAseTplFieldGroup,
  selectAseFieldInstanceChildInstanceIdsAst,
  selectAseIsEditable,
  selectAseFieldInstanceAseMerged,
} from 'store/assessments/selectors';
import { UUID } from 'types/types';

type Props = {
  // The instance ID that holds the instances (e.g. Flat)
  parentInstanceId: string | null;
  // The field ID of the parent instance that holds the results
  parentFieldId: string;
  // The group that is added to the parent (e.g. Flat Door)
  childGroupId: string;
  // Lite repeatable IDs
  value: UUID[];
};

export const ChildGroupLightAssessment: React.FC<Props> = ({
  parentFieldId,
  parentInstanceId,
  childGroupId,
  value: childInstanceIds,
}) => {
  const dispatch = useDispatch();

  const isEditable = useSelector(selectAseIsEditable);

  const childGroup = useSelector((state) =>
    selectAseTplFieldGroup(state, childGroupId),
  );

  const parentGroup = useSelector((state) =>
    selectAseTplFieldToGroup(state, parentFieldId),
  );

  const assetChildInstanceIds =
    useSelector((state) =>
      selectAseFieldInstanceChildInstanceIdsAst(state, {
        fieldId: parentFieldId,
        instanceId: parentInstanceId,
      }),
    ) || [];

  const canSave = !!(
    useSelector((state) =>
      selectAseFieldInstanceAseMerged(state, parentInstanceId || '-1'),
    ) || !parentInstanceId
  );

  const deletedChildInstanceIds = isEditable
    ? difference(assetChildInstanceIds, childInstanceIds)
    : [];

  const handlePressAddInstance = () => {
    if (childGroup) {
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.AseLightRepeatableInstance,
          params: {
            groupId: childGroup.id,
            instanceId: undefined,
            parent: { fieldId: parentFieldId, instanceId: parentInstanceId },
          },
        }),
      );
    }
  };

  const reorderKey = React.useRef(randomKey());
  const handleStartReorder = () => {
    if (childGroup) {
      dispatch(
        doAseReorderChildGroups({
          groupId: childGroup.id,
          instanceIds: childInstanceIds,
          eventKey: reorderKey.current,
        }),
      );
    }
  };

  React.useEffect(() => {
    const reorderEvent = doAddEvent(eventReorderScreenDone, (action) => {
      if (action.payload.key === reorderKey.current) {
        dispatch(
          doAseSetChildGroups({
            parent: {
              fieldId: parentFieldId,
              instanceId: parentInstanceId,
            },
            instanceIds: action.payload.stringValues,
          }),
        );
      }
    });
    return () => {
      doRemoveEvent(reorderEvent);
    };
  }, [dispatch, parentFieldId, parentInstanceId, reorderKey]);

  if (!childGroup || !parentGroup) {
    return null;
  }

  return (
    <>
      {!!childInstanceIds.length || !!deletedChildInstanceIds.length ? (
        <Card
          label={`${childGroup.name}`}
          style={
            isEditable && canSave
              ? { marginBottom: theme.paddingHorizontalHalf }
              : undefined
          }
          labelLinkText="Reorder"
          onLabelLinkPress={
            isEditable && canSave && childInstanceIds.length > 1
              ? handleStartReorder
              : undefined
          }
          explanation={
            isEditable && !canSave
              ? `Save this ${parentGroup.calculatedInfo.singularName.toLowerCase()} to manage ${childGroup.name.toLowerCase()}`
              : undefined
          }
        >
          {[...childInstanceIds, ...deletedChildInstanceIds].map(
            (childInstanceId) => (
              <LightRepeatableListItem
                key={childInstanceId}
                instanceId={childInstanceId}
                isEditable={isEditable && canSave}
                isDeleted={deletedChildInstanceIds.includes(childInstanceId)}
                isViewable={isEditable ? canSave : true}
                parent={{
                  fieldId: parentFieldId,
                  instanceId: parentInstanceId,
                }}
              />
            ),
          )}
        </Card>
      ) : (
        (!isEditable || !canSave) && (
          <Card
            label={childGroup.name}
            explanation={
              isEditable && !canSave
                ? `Save this ${parentGroup.calculatedInfo.singularName.toLowerCase()} to manage ${childGroup.name.toLowerCase()}`
                : undefined
            }
          >
            <CardRow placeholder={`No ${childGroup.name}`} />
          </Card>
        )
      )}
      {isEditable && canSave && (
        <Card
          label={
            !childInstanceIds.length && !deletedChildInstanceIds.length
              ? childGroup.name
              : undefined
          }
        >
          <CardRow
            label={`Add ${childGroup.calculatedInfo.singularName}`}
            icon={ICONS.PlusLight}
            labelColor={theme.colors.blue}
            iconColor={theme.colors.blue}
            onPress={handlePressAddInstance}
          />
        </Card>
      )}
    </>
  );
};
